import { useCallback, useEffect, useState } from "react";
import { Box, Button as UIButton } from "@mui/material";
import { useParams } from "react-router-dom";
import StickyNoteLineIcon from "remixicon-react/StickyNoteLineIcon";
import CalendarCheckFillIcon from "remixicon-react/CalendarCheckFillIcon";
import TrashLineIcon from "remixicon-react/DeleteBin2LineIcon";
import Edit2LineIcon from "remixicon-react/Edit2LineIcon";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useForm, Controller } from "react-hook-form";
import dateFormat from "dateformat";
import { useQuery } from "@tanstack/react-query";
import { Text, Layout, InfoContainer, Popin, Button } from "$components";
import { EntrepotService, UserServices } from "$services";
import useUserForm from "$hooks/useUserForm";
import UserForm from "$containers/Coordinators/UserForm";
import JModal from "$components/JModal/JModal";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";

import { useEntrepotQuery } from "$hooks/useEntrepotQuery";
import useAuth from "$hooks/useAuth";
import FileUploader from "$components/FileUploader/FileUploader";
import EntrepotForm from "./EntrepotForm";
import Contacts from "./Contacts";
import useStyles from "./useStyles";
import Activities from "./Activities";
import EntrepotInfo from "./EntrepotInfo";
import { CacheInvalidationAction } from "$utils/cache";
import { DeleteUser } from "$services/Coordinators.service";

function EntrepotDetails() {
  const param = useParams<"id" | "idEntrepot">();
  const { idEntrepot, id: idCompany } = param;
  const classes = useStyles();
  const { isAdmin, token } = useAuth();

  const {
    onAddClick: onAddUser,
    onEditClick: onEditUser,
    user,
    setUser,
    open: openUser,
    togglePopin,
    onSubmitContact,
    onSubmitEdit: onSubmitEditUser,
    error: errorUser,
    title,
    setTitle,
  } = useUserForm(null, idEntrepot);

  const { data: entrepot, refetch: refetchEntrepot } =
    useEntrepotQuery(idEntrepot);

  const [open, toggleOpen] = useState<boolean>(false);
  const [entrepotError] = useState<string>(null);

  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] =
    useState<boolean>(false);
  const [formValues, setFormValues] = useState<any>({
    medias: (entrepot?.medias || []).map((media: any) => media || {}),
  });

  const { control, setValue } = useForm({
    mode: "onChange",
  });

  const { createdAt, notes } = entrepot || { location: {} };

  const { data: warehouseUsers } = useQuery({
    queryKey: [CacheInvalidationAction.GET_USERS, idEntrepot],
    queryFn: () => UserServices.FetchUsers(token, { warehouse: idEntrepot }),
    gcTime: 5 * 60 * 1000,
    enabled: !!token && !!idEntrepot,
  });

  useEffect(() => {
    setFormValues({
      medias: (entrepot?.medias || []).map((media: any) => media || {}),
    });
  }, [entrepot]);

  // MODULE SYNC DISABLED

  useEffect(() => {
    setValue("medias", formValues.medias, { shouldValidate: false });
  }, [formValues.medias, setValue, formValues]);

  const handleDeleteUser = useCallback(async () => {
    await DeleteUser(token, user?._id);
    setIsDeleteUserModalOpen(false);
    togglePopin(false);
  }, [setIsDeleteUserModalOpen, togglePopin, token, user?._id]);

  const onSubmit = async (data: any) => {
    const result = await EntrepotService.updateEntrepot(
      token,
      idEntrepot || "",
      data
    );
    if (result?._id) {
      toggleOpen(false);
      refetchEntrepot();
    }
  };

  const onEditClick = () => {
    toggleOpen(true);
  };

  if (!idEntrepot) {
    return null;
  }

  return (
    <Layout
      title={entrepot?.name || ""}
      renderHead={() => (
        <div className={classes.header}>
          <Box display="flex" alignItems="center">
            {createdAt && (
              <div className={classes.createdAtBlock}>
                <CalendarCheckFillIcon className={classes.calendarIcon} />
                Créée le{" "}
                {createdAt &&
                  dateFormat(new Date(createdAt), DEFAULT_DATE_FORMAT)}
              </div>
            )}
          </Box>
        </div>
      )}
    >
      <div className={classes.containerInfo} style={{ display: "flex" }}>
        <EntrepotInfo entrepot={entrepot} onEditClick={onEditClick} />
        <Activities entrepot={entrepot} onEditClick={onEditClick} />
      </div>

      <div className={classes.bottomBlocks}>
        <Contacts
          users={warehouseUsers}
          handleAddUserClick={() => {
            setTitle("Ajouter un contact");
            onAddUser();
          }}
          handleEditUserClick={(user) => {
            setTitle("Édition du contact");
            onEditUser(user);
          }}
        />

        <InfoContainer
          leftHeader={
            <div className={classes.infoHeader}>
              <AttachFileIcon />
              <Text type="h3">Pièces jointes</Text>
            </div>
          }
        >
          <form>
            <Controller
              control={control}
              name="medias"
              render={({ field: { onChange, value } }) => (
                <FileUploader
                  value={value}
                  onChange={(values) => {
                    onChange(values);
                  }}
                  isAdmin={isAdmin()}
                  type="entrepot"
                  company={entrepot?._id}
                  id={entrepot?._id ?? undefined}
                  isEdit={false}
                  isLargeVersion
                />
              )}
            />
          </form>
        </InfoContainer>

        <InfoContainer
          leftHeader={
            <div className={classes.infoHeader}>
              <StickyNoteLineIcon />
              <Text type="h3">Notes</Text>
            </div>
          }
          rightHeader={
            <UIButton
              className={classes.editButton}
              onClick={onEditClick}
              style={{ textTransform: "none" }}
            >
              <Edit2LineIcon />
            </UIButton>
          }
        >
          <Text>{notes || <i>Aucune note saisie</i>}</Text>
        </InfoContainer>
      </div>

      <Popin
        openPopin={open}
        toggleDrawer={toggleOpen}
        containerClassName={classes.popin}
      >
        <Text type="h2">Édition de l&apos;entrepôt</Text>
        <EntrepotForm
          parentCompanyId={idCompany ? entrepot?.parentCompany : undefined}
          entrepot={entrepot}
          onSubmit={onSubmit}
          onReset={() => toggleOpen(false)}
          entrepotError={entrepotError}
        />
      </Popin>

      <Popin
        customCloseAction={() => {
          setUser(null);
        }}
        openPopin={openUser}
        toggleDrawer={togglePopin}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Text type="h2">{title}</Text>
          {user && isAdmin && (
            <Button
              color="primary"
              onClick={() => setIsDeleteUserModalOpen(true)}
            >
              <TrashLineIcon />
            </Button>
          )}
        </Box>
        <UserForm
          user={user}
          onSubmit={user ? onSubmitEditUser : onSubmitContact}
          onReset={() => togglePopin(false)}
          isCoordinator={false}
          canHaveTwoPhones={false}
          error={errorUser}
          canBeUser={false}
        />
      </Popin>

      {isDeleteUserModalOpen && (
        <JModal
          message={`Êtes vous sur de vouloir supprimer l'utilisateur : ${user.lastname} ${user.firstname} (${user.email})`}
          labelOk="Supprimer"
          labelCancel="Annuler"
          onConfirm={handleDeleteUser}
          onClose={() => setIsDeleteUserModalOpen(false)}
        />
      )}
    </Layout>
  );
}

export default EntrepotDetails;
